type GtagCommand = 'js' | 'config' | 'event' | 'set';

type Gtag = (command: GtagCommand, target: string | Date | Record<string, unknown>, params?: Record<string, unknown>) => void;

type AnalyticsParamValue = string | number | undefined;

type AnalyticsParams = Record<string, AnalyticsParamValue>;

export type PublicPageGroup = 'marketing' | 'account' | 'report' | 'legal' | 'public';

export type PublicPageType =
  | 'home'
  | 'methodology'
  | 'pricing'
  | 'reports_login'
  | 'reports_access'
  | 'reports_library'
  | 'compare_reports'
  | 'full_report'
  | 'report_conclusion'
  | 'report_savings_guide'
  | 'shared_report'
  | 'privacy_policy'
  | 'terms_of_service'
  | 'cookie_policy'
  | 'refund_policy'
  | 'not_found'
  | 'generic_public';

export type AddressSearchOutcome =
  | 'empty_input'
  | 'invalid_address'
  | 'unsupported_state'
  | 'existing_report'
  | 'lead_modal'
  | 'lead_modal_after_check_error';

export type LeadFormSource = 'address_search' | 'quick_start' | 'direct';

export type ReportAccessMethod = 'email' | 'google_identity';

export type ReportAccessResult =
  | 'success'
  | 'missing_email'
  | 'missing_turnstile'
  | 'turnstile_not_configured'
  | 'rate_limited'
  | 'invalid_request'
  | 'not_found'
  | 'server_error'
  | 'error';

export type ReportPageType = 'full_report' | 'report_conclusion';

export type PageViewMeta = {
  pageTitle: string;
  pageGroup: PublicPageGroup;
  pageType: PublicPageType;
  pageTemplate: string;
  profile?: string | null;
  pageReferrer?: string | null;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

const GA_SCRIPT_ID = 'scorenest-ga4';
const GA_OPT_OUT_KEY = 'scorenest:ga4-opt-out';
const MAX_PARAM_LENGTH = 100;
const DEFAULT_MEASUREMENT_ID = 'G-Y1B8X2DYNB';
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || DEFAULT_MEASUREMENT_ID;
const ALLOW_DEV = import.meta.env.VITE_GA_ALLOW_DEV === 'true';

let initializedMeasurementId: string | null = null;

function isBrowser(): boolean {
  return typeof globalThis !== 'undefined' && typeof globalThis.window !== 'undefined';
}

function isTrackingEnvironmentAllowed(): boolean {
  if (!MEASUREMENT_ID) return false;
  if (import.meta.env.PROD) return true;
  return ALLOW_DEV;
}

function getDisableKey(measurementId: string): `ga-disable-${string}` {
  return `ga-disable-${measurementId}`;
}

function isDoNotTrackEnabled(): boolean {
  if (!isBrowser()) return false;

  const navigatorDnt = globalThis.navigator?.doNotTrack;
  const windowDnt = (globalThis.window as Window & { doNotTrack?: string }).doNotTrack;
  return navigatorDnt === '1' || navigatorDnt === 'yes' || windowDnt === '1';
}

function isAnalyticsOptedOut(): boolean {
  if (!isBrowser()) return false;

  try {
    return globalThis.window.localStorage.getItem(GA_OPT_OUT_KEY) === 'true';
  } catch {
    return false;
  }
}

function setAnalyticsDisabled(disabled: boolean): void {
  if (!isBrowser() || !MEASUREMENT_ID) return;
  globalThis.window[getDisableKey(MEASUREMENT_ID)] = disabled;
}

function ensureDataLayer(): void {
  const browserWindow = globalThis.window;
  browserWindow.dataLayer = browserWindow.dataLayer ?? [];

  if (!browserWindow.gtag) {
    browserWindow.gtag = function gtag(_command, _target, _params) {
      browserWindow.dataLayer?.push(arguments);
    };
  }
}

function injectGaScript(): void {
  const browserDocument = globalThis.document;
  if (browserDocument.getElementById(GA_SCRIPT_ID)) return;

  const script = browserDocument.createElement('script');
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  browserDocument.head.appendChild(script);
}

function clampString(value: string, maxLength = MAX_PARAM_LENGTH): string {
  const normalized = value.trim();
  if (normalized.length <= maxLength) return normalized;
  return normalized.slice(0, maxLength);
}

function normalizeParamValue(value: AnalyticsParamValue): string | number | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  return clampString(String(value));
}

function normalizeParams(params: AnalyticsParams): Record<string, string | number> {
  return Object.entries(params).reduce<Record<string, string | number>>((acc, [key, value]) => {
    const normalized = normalizeParamValue(value);
    if (normalized === undefined) return acc;
    acc[key] = normalized;
    return acc;
  }, {});
}

export function buildGa4PageLocation(pageTemplate: string, profile?: string | null): string {
  const browserWindow = globalThis.window;
  const url = new URL(pageTemplate.startsWith('/') ? pageTemplate : `/${pageTemplate}`, browserWindow.location.origin);

  if (profile?.trim()) {
    url.searchParams.set('profile', profile.trim().toLowerCase());
  }

  return url.toString();
}

function ensureGa4(): boolean {
  if (!isBrowser()) return false;
  if (!isTrackingEnvironmentAllowed()) return false;

  const disabled = isAnalyticsOptedOut() || isDoNotTrackEnabled();
  setAnalyticsDisabled(disabled);
  if (disabled) return false;

  ensureDataLayer();
  injectGaScript();

  if (initializedMeasurementId === MEASUREMENT_ID) {
    return true;
  }

  globalThis.window.gtag?.('js', new Date());
  globalThis.window.gtag?.('config', MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  initializedMeasurementId = MEASUREMENT_ID;
  return true;
}

function trackEvent(eventName: string, params: AnalyticsParams = {}): void {
  if (!ensureGa4()) return;

  globalThis.window.gtag?.('event', eventName, normalizeParams(params));
}

export function setGa4OptOut(disabled: boolean): void {
  if (!isBrowser()) return;

  try {
    globalThis.window.localStorage.setItem(GA_OPT_OUT_KEY, disabled ? 'true' : 'false');
  } catch {
    // Best-effort only.
  }

  setAnalyticsDisabled(disabled);
}

export function trackGa4PageView(meta: PageViewMeta): void {
  const pageLocation = buildGa4PageLocation(meta.pageTemplate, meta.profile);
  const pageReferrer =
    meta.pageReferrer && meta.pageReferrer.trim()
      ? meta.pageReferrer.startsWith('http')
        ? meta.pageReferrer
        : buildGa4PageLocation(meta.pageReferrer)
      : undefined;

  trackEvent('page_view', {
    page_title: meta.pageTitle,
    page_location: pageLocation,
    page_referrer: pageReferrer,
    page_group: meta.pageGroup,
    page_type: meta.pageType,
    page_template: meta.pageTemplate,
    report_profile: meta.profile?.trim()?.toLowerCase() || undefined,
  });
}

export function trackAddressSearch(outcome: AddressSearchOutcome): void {
  trackEvent('address_search', {
    surface: 'home_hero',
    outcome,
  });
}

export function trackLeadFormOpened(params: {
  source: LeadFormSource;
  hasPrefilledAddress: boolean;
  hasSavedDefaults: boolean;
}): void {
  trackEvent('lead_form_opened', {
    source: params.source,
    has_prefilled_address: params.hasPrefilledAddress ? 'true' : 'false',
    has_saved_defaults: params.hasSavedDefaults ? 'true' : 'false',
  });
}

export function trackLeadSubmitted(params: {
  profileUi: string;
  priorityCount: number;
  helperCount: number;
}): void {
  trackEvent('generate_lead', {
    form_name: 'home_lead_modal',
    profile_ui: params.profileUi,
    priority_count: params.priorityCount,
    helper_count: params.helperCount,
  });
}

export function trackLeadSubmitError(params: {
  profileUi: string;
  reason: string;
}): void {
  trackEvent('lead_submit_error', {
    form_name: 'home_lead_modal',
    profile_ui: params.profileUi,
    reason: params.reason,
  });
}

export function trackReportAccessRequest(params: {
  method: ReportAccessMethod;
  result: ReportAccessResult;
}): void {
  trackEvent('report_access_request', {
    method: params.method,
    result: params.result,
  });
}

function toScoreBand(score: number | null | undefined): string | undefined {
  if (typeof score !== 'number' || !Number.isFinite(score)) return undefined;

  const rounded = Math.max(0, Math.min(100, Math.round(score)));
  const bandStart = Math.floor(rounded / 10) * 10;
  const bandEnd = bandStart === 100 ? 100 : bandStart + 9;
  return `${bandStart}-${bandEnd}`;
}

export function trackReportViewed(params: {
  pageType: ReportPageType;
  profile: string;
  overallScore: number | null | undefined;
  isStale: boolean;
}): void {
  trackEvent('view_report', {
    page_type: params.pageType,
    profile: params.profile,
    score_band: toScoreBand(params.overallScore),
    is_stale: params.isStale ? 'true' : 'false',
  });
}
